import React, { FC, useEffect, useRef, useState, useCallback } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { PaygapMetricItemDto } from "@green0meter/api-client";

import { AnalysisParameters, usePaygapAnalysis, useAutoPopulateAnalysisParams } from "../../hooks";
import { AnalysisParamsOverwriteDialog, EducationYearsMissingDialog } from "../PaygapDataUpload/components";

import { Button } from "@components/base";

interface PaygapAnalysisActionsProps {
  startAnalysisButtonText: string;
  parameters: AnalysisParameters;
  hasValidData: boolean;
  hasIdConflicts: boolean;
  importPersonnelError: Error | null;
  dataFetchedFromDatabase: boolean;
  onAnalysisComplete?: (results: PaygapMetricItemDto[]) => void;
  onManageEmployeeData?: () => void;
  onOpenAnalysisParameters?: () => void;
  isLoadingParameters?: boolean;
}

const PaygapAnalysisActions: FC<PaygapAnalysisActionsProps> = ({
  startAnalysisButtonText,
  parameters,
  hasValidData,
  hasIdConflicts,
  importPersonnelError,
  dataFetchedFromDatabase,
  onAnalysisComplete,
  onManageEmployeeData,
  onOpenAnalysisParameters,
  isLoadingParameters = false,
}) => {
  const { t } = useTranslation();

  const {
    analysisError,
    isFetchingMetrics,
    metricsError,
    showAnalysisConfirmation,
    showRequestSent,
    handleStartAnalysis,
    handleFetchMetrics,
    testNetworkConnectivity,
    setShowAnalysisConfirmation,
  } = usePaygapAnalysis();

  // Auto-populate analysis parameters hook
  const autoPopulate = useAutoPopulateAnalysisParams();

  // Track if we should run analysis after overwrite dialog closes
  const [pendingAnalysisRun, setPendingAnalysisRun] = useState(false);

  // Auto-fetch metrics on page load
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      handleFetchMetrics(onAnalysisComplete);
    }
  // Intentionally run only once on mount
  }, []);

  const handleAnalysisConfirmationCancel = (): void => {
    setShowAnalysisConfirmation(false);
  };

  const runAnalysis = useCallback(async (): Promise<void> => {
    await handleStartAnalysis(
      parameters,
      hasValidData,
      onAnalysisComplete || ((): void => {}),
    );
  }, [handleStartAnalysis, parameters, hasValidData, onAnalysisComplete]);

  const proceedAfterMissingYearsCheck = useCallback(async (): Promise<void> => {
    // Check for missing education years (async to get fresh data)
    const hasMissingYears = await autoPopulate.checkForMissingYears();

    if (hasMissingYears) {
      // Dialog will be shown, keep pendingAnalysisRun true
      return;
    }

    // No missing years, proceed with analysis
    setPendingAnalysisRun(false);
    await runAnalysis();
  }, [autoPopulate, runAnalysis]);

  const handleAnalysisConfirmationRun = async (): Promise<void> => {
    setShowAnalysisConfirmation(false);

    // Check and populate analysis params first
    const needsConfirmation = await autoPopulate.checkAndPopulate();

    if (needsConfirmation) {
      // Overwrite dialog will be shown, mark that we need to run analysis after
      setPendingAnalysisRun(true);

return;
    }

    // No overwrite dialog needed, check for missing years
    setPendingAnalysisRun(true);
    await proceedAfterMissingYearsCheck();
  };

  const handleOverwriteConfirm = async (): Promise<void> => {
    await autoPopulate.confirmOverwrite();
    if (pendingAnalysisRun) {
      await proceedAfterMissingYearsCheck();
    }
  };

  const handleOverwriteCancel = async (): Promise<void> => {
    autoPopulate.closeOverwriteDialog();
    // Still proceed with missing years check even if user keeps existing params
    if (pendingAnalysisRun) {
      await proceedAfterMissingYearsCheck();
    }
  };

  const handleMissingYearsOpenParameters = (): void => {
    autoPopulate.closeMissingYearsDialog();
    setPendingAnalysisRun(false);
    if (onOpenAnalysisParameters) {
      onOpenAnalysisParameters();
    }
  };

  const handleMissingYearsCancel = (): void => {
    autoPopulate.closeMissingYearsDialog();
    setPendingAnalysisRun(false);
  };

  const handleOpenConfirm = (): void => {
    setShowAnalysisConfirmation(true);
  };

  const handleAnalysisConfirmationSeeData = (): void => {
    setShowAnalysisConfirmation(false);
    // Call the parent component's callback to open the Manage Employee Data drawer
    if (onManageEmployeeData) {
      onManageEmployeeData();
    }
  };

  return (
    <>
      {analysisError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{t("paygap.analysis.error")}</strong> {analysisError}
          </Typography>
        </Alert>
      )}

      {metricsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{t("paygap.analysis.metricsError")}</strong> {metricsError}
          </Typography>
        </Alert>
      )}

      {showRequestSent && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">{t("paygap.analysis.running")}</Typography>
        </Alert>
      )}

      <Box mt={3} display="flex" gap={2} flexWrap="wrap" alignItems="center">
        <Button
          variant="contained"
          color="primary"
          disabled={
            hasIdConflicts ||
            !hasValidData ||
            (!isLoadingParameters && !parameters.referenceYear)
          }
          size="large"
          onClick={handleOpenConfirm}
        >
          {startAnalysisButtonText}
        </Button>
      </Box>

      {importPersonnelError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>{t("paygap.analysis.importError")}</strong>{" "}
            {importPersonnelError.message}
          </Typography>
        </Alert>
      )}

      {dataFetchedFromDatabase && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>{t("paygap.analysis.dataSource")}</strong>{" "}
            {t("paygap.analysis.databaseSource")}
            {t("paygap.analysis.databaseNote")}
          </Typography>
        </Alert>
      )}

      {/* Analysis Confirmation Dialog */}
      <Dialog
        open={showAnalysisConfirmation}
        onClose={handleAnalysisConfirmationCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{startAnalysisButtonText}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {!hasValidData
              ? t("paygap.analysis.confirm.noData")
              : t("paygap.analysis.confirm.withData")}
          </Typography>

          {!hasValidData && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>{t("paygap.dialog.note")}</strong>{" "}
                {t("paygap.analysis.confirm.note")}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAnalysisConfirmationCancel} color="secondary">
            {t("paygap.analysis.confirm.cancel")}
          </Button>
          {!hasValidData && (
            <Button
              onClick={handleAnalysisConfirmationSeeData}
              variant="outlined"
              color="primary"
            >
              {t("paygap.analysis.confirm.seeData")}
            </Button>
          )}
          <Button
            onClick={handleAnalysisConfirmationRun}
            variant="contained"
            color="primary"
          >
            {t("paygap.analysis.confirm.run")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Analysis Parameters Auto-Populate Confirmation Dialog */}
      <AnalysisParamsOverwriteDialog
        open={autoPopulate.showOverwriteDialog}
        existingParams={autoPopulate.existingParams}
        newParams={autoPopulate.pendingParams}
        onConfirm={handleOverwriteConfirm}
        onCancel={handleOverwriteCancel}
      />

      {/* Education Missing Years Dialog */}
      <EducationYearsMissingDialog
        open={autoPopulate.showMissingYearsDialog}
        onOpenParameters={handleMissingYearsOpenParameters}
        onCancel={handleMissingYearsCancel}
      />
    </>
  );
};

export default PaygapAnalysisActions;
